import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  Box,
  Text,
  Badge,
  HStack,
  Button,
  useColorModeValue,
  useToast,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ApiConsume } from '../../_API/ApiConsume';
import axios from '../../_API/axiosConfig';
import { isNativePushSupported } from '../../_Utils/pushNotifications';
import { openUrl } from '../../_Utils/mobileBridge';

const NotificationsCenter = ({ isOpen, onClose, notifications = [], counts = {}, onRefresh }) => {
  const bg = useColorModeValue('white', 'gray.900');
  const dotColor = useColorModeValue('blue.500', 'blue.300');
  const cardBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const headingColor = useColorModeValue('gray.800', 'gray.100');
  const bodyColor = useColorModeValue('gray.700', 'gray.200');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const [activeTab, setActiveTab] = useState(0);
  const [localItems, setLocalItems] = useState(() => notifications || []);
  const [loadingKeys, setLoadingKeys] = useState({});
  const toast = useToast();
  const dismissDisclosure = useDisclosure();
  const cancelRef = useRef();
  const [pendingDismiss, setPendingDismiss] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const tabKeys = ['operativas', 'sistema', 'mensajes'];
  const isNative = isNativePushSupported();
  const navigate = useNavigate();
  const triggerRefresh = useCallback(async () => {
    if (typeof onRefresh !== 'function') return;
    try {
      await onRefresh();
    } catch (error) {
      console.error('Notifications refresh failed', error);
    }
  }, [onRefresh]);

  useEffect(() => {
    setLocalItems(notifications || []);
  }, [notifications]);

  const setLoadingForKey = (key, isLoading) => {
    setLoadingKeys(prev => {
      if (isLoading) {
        return { ...prev, [key]: true };
      }
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const getKey = (item, fallback) => item?.key || item?.id || fallback;

  const grouped = useMemo(() => {
    const base = { operativas: [], sistema: [], mensajes: [] };
    (localItems || []).forEach((item) => {
      const tab = (item.tab || 'operativas').toLowerCase();
      if (base[tab]) base[tab].push(item);
    });
    return base;
  }, [localItems]);

  const currentTabKey = tabKeys[activeTab] || 'operativas';
  const currentItems = grouped[currentTabKey] || [];
  const currentUnreadKeys = currentItems
    .filter((item) => (item.state || 'unread') !== 'read')
    .map((item) => getKey(item))
    .filter(Boolean);
  const currentReadKeys = currentItems
    .filter((item) => (item.state || 'unread') === 'read')
    .map((item) => getKey(item))
    .filter(Boolean);

  const derivedCounts = useMemo(() => {
    const result = { operativas: 0, sistema: 0, mensajes: 0, total: 0 };
    (localItems || []).forEach((item) => {
      const tab = (item.tab || 'operativas').toLowerCase();
      if (result[tab] !== undefined) {
        result[tab] += 1;
      }
      result.total += 1;
    });
    return result;
  }, [localItems]);

  const openLink = (url) => {
    if (!url) return;
    if (/^https?:\/\//i.test(url)) {
      openUrl(url);
    } else if (/^\/(?!\/)/.test(url)) {
      // Ruta interna: router, sin recargar la app.
      navigate(url);
      onClose?.();
    } else {
      window.location.assign(url);
    }
  };

  const persistState = async (key, nextState) => {
    return ApiConsume('META__NOTIFICATIONS_STATE', 'POST', { key, state: nextState });
  };

  const bulkPersistState = async (keys, nextState) => {
    return ApiConsume('META__NOTIFICATIONS_STATE_BULK', 'POST', { keys, state: nextState });
  };

  const handleToggleRead = async (item, forcedState) => {
    const key = getKey(item);
    if (!key) return;
    const nextState = forcedState || (item.state === 'read' ? 'unread' : 'read');
    try {
      setLoadingForKey(key, true);
      await persistState(key, nextState);
      setLocalItems(prev =>
        prev.map((notif) =>
          getKey(notif) === key ? { ...notif, state: nextState } : notif
        )
      );
      await triggerRefresh();
    } catch (error) {
      toast({ title: 'No se pudo actualizar la notificación.', status: 'error' });
    } finally {
      setLoadingForKey(key, false);
    }
  };

  const handleTestPush = async () => {
    if (!isNative) return;
    try {
      await axios.post('/push/test/');
      toast({ title: 'Push de prueba enviado', status: 'success' });
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message;
      toast({
        title: 'No se pudo enviar el push',
        description: detail,
        status: 'error',
      });
    }
  };

  const reinjectItem = (item) => {
    if (!item) return;
    const key = getKey(item);
    setLocalItems(prev => {
      if (prev.some(existing => getKey(existing) === key)) {
        return prev;
      }
      const next = [...prev, item];
      next.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return next;
    });
  };

  const performDismiss = async (item) => {
    const key = getKey(item);
    if (!key) return;
    setLoadingForKey(key, true);
    setLocalItems(prev => prev.filter((notif) => getKey(notif) !== key));
    try {
      await persistState(key, 'dismissed');
      await triggerRefresh();
    } catch (error) {
      toast({ title: 'No se pudo ocultar la notificación.', status: 'error' });
      reinjectItem(item);
    } finally {
      setLoadingForKey(key, false);
    }
  };

  const handleDismiss = (item) => {
    const severity = (item.severity || '').toLowerCase();
    if (['warning', 'critical'].includes(severity)) {
      setPendingDismiss(item);
      dismissDisclosure.onOpen();
      return;
    }
    performDismiss(item);
  };

  const confirmDismiss = () => {
    if (pendingDismiss) {
      performDismiss(pendingDismiss);
      setPendingDismiss(null);
    }
    dismissDisclosure.onClose();
  };

  const cancelDismiss = () => {
    setPendingDismiss(null);
    dismissDisclosure.onClose();
  };

  const handleOpen = async (item) => {
    // Primero se marca leída: navegar antes podía cortar el POST.
    // handleToggleRead atrapa el error (toast), así que un fallo no frena la navegación.
    if (item?.state !== 'read') {
      await handleToggleRead(item, 'read');
    }
    if (item?.url) {
      openLink(item.url);
    }
  };

  const handleBulkAction = async (keys, nextState) => {
    if (!keys || keys.length === 0) return;
    const keySet = new Set(keys);
    try {
      setBulkAction(nextState);
      setBulkLoading(true);
      await bulkPersistState(keys, nextState);
      setLocalItems((prev) => {
        if (!Array.isArray(prev)) return prev;
        if (nextState === 'dismissed') {
          return prev.filter((notif) => !keySet.has(getKey(notif)));
        }
        return prev.map((notif) =>
          keySet.has(getKey(notif)) ? { ...notif, state: nextState } : notif
        );
      });
      await triggerRefresh();
      toast({
        title: nextState === 'dismissed' ? 'Notificaciones limpiadas.' : 'Notificaciones actualizadas.',
        status: 'success',
      });
    } catch (error) {
      toast({ title: 'No se pudo actualizar las notificaciones.', status: 'error' });
    } finally {
      setBulkLoading(false);
      setBulkAction(null);
    }
  };

  const handleBulkMarkRead = () => handleBulkAction(currentUnreadKeys, 'read');
  const handleBulkDismissRead = () => handleBulkAction(currentReadKeys, 'dismissed');

  const renderList = (items, emptyLabel) => {
    if (!items || items.length === 0) {
      return (
        <Box py={10} textAlign="center">
          <Text color={mutedText}>{emptyLabel}</Text>
        </Box>
      );
    }

    return (
      <VStack align="stretch" spacing={3}>
        {items.map((item, idx) => {
          const key = getKey(item, idx);
          const isRead = (item.state || 'unread') === 'read';
          const isLoading = !!loadingKeys[key];
          return (
            <Box
              key={key}
              borderWidth="1px"
              borderRadius="lg"
              p={3}
              bg={cardBg}
              borderColor={borderColor}
              opacity={isRead ? 0.75 : 1}
            >
              <HStack justify="space-between" mb={1}>
                <Text fontWeight="bold" fontSize="sm" color={headingColor}>
                  {item.title || 'Notificación'}
                </Text>
                <HStack spacing={2}>
                  {isRead && (
                    <Badge colorScheme="gray" variant="subtle">
                      Leída
                    </Badge>
                  )}
                  <Badge colorScheme={item.severity === 'critical' ? 'red' : item.severity === 'warning' ? 'orange' : 'blue'}>
                    {item.severity || 'info'}
                  </Badge>
                </HStack>
              </HStack>
              <Text fontSize="sm" color={bodyColor}>
                {item.body || 'Sin descripción'}
              </Text>
              {item.created_at && (
                <Text fontSize="xs" color={mutedText} mt={1}>
                  {new Date(item.created_at).toLocaleString()}
                </Text>
              )}
              <HStack spacing={3} mt={3}>
                <Button
                  size="xs"
                  colorScheme="blue"
                  variant="solid"
                  onClick={() => handleOpen(item)}
                  isDisabled={!item.url}
                >
                  Abrir
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => handleToggleRead(item)}
                  isLoading={isLoading}
                >
                  {isRead ? 'Marcar como no leída' : 'Marcar como leída'}
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleDismiss(item)}
                >
                  No me interesa
                </Button>
              </HStack>
            </Box>
          );
        })}
      </VStack>
    );
  };

  return (
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent bg={bg}>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
            <HStack justify="space-between">
              <Text fontWeight="bold" color={headingColor}>Centro de notificaciones</Text>
              <Badge colorScheme="blue">{derivedCounts.total || counts?.total || 0} pendientes</Badge>
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            <HStack justify="flex-end" spacing={3} mb={4}>
              <Button
                size="sm"
                variant="outline"
                onClick={handleBulkMarkRead}
                isDisabled={!currentUnreadKeys.length}
                isLoading={bulkLoading && bulkAction === 'read'}
              >
                Marcar todas como leídas
              </Button>
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={handleBulkDismissRead}
                isDisabled={!currentReadKeys.length}
                isLoading={bulkLoading && bulkAction === 'dismissed'}
              >
                Limpiar leídas
              </Button>
              {isNative && (
                <Button size="sm" colorScheme="purple" onClick={handleTestPush}>
                  Probar push
                </Button>
              )}
            </HStack>
            <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed">
              <TabList>
                <Tab color={bodyColor}>
                  Operativas <Badge ml={2}>{grouped.operativas.length}</Badge>
                </Tab>
                <Tab color={bodyColor}>
                  Sistema <Badge ml={2}>{grouped.sistema.length}</Badge>
                </Tab>
                <Tab color={bodyColor}>
                  Mensajes <Badge ml={2}>{grouped.mensajes.length}</Badge>
                </Tab>
              </TabList>
              <TabPanels mt={4}>
                <TabPanel>
                  {renderList(grouped.operativas, 'Sin notificaciones operativas.')}
                </TabPanel>
                <TabPanel>
                  {renderList(grouped.sistema, 'Sin avisos del sistema.')}
                </TabPanel>
                <TabPanel>
                  {renderList(grouped.mensajes, 'No hay mensajes nuevos.')}
                </TabPanel>
              </TabPanels>
            </Tabs>
            <Box mt={6}>
              <Button
                width="100%"
                colorScheme="blue"
                leftIcon={
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={dotColor}
                  />
                }
                onClick={onClose}
              >
                Cerrar
              </Button>
            </Box>
          </DrawerBody>
        </DrawerContent>
        <AlertDialog
          isOpen={dismissDisclosure.isOpen}
          leastDestructiveRef={cancelRef}
          onClose={cancelDismiss}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Confirmar acción
              </AlertDialogHeader>

              <AlertDialogBody>
                ¿Seguro que querés ocultar esta notificación? Podrías perder un aviso importante.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={cancelDismiss}>
                  Cancelar
                </Button>
                <Button colorScheme="red" onClick={confirmDismiss} ml={3}>
                  Ocultar
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Drawer>
  );
};

export default NotificationsCenter;
